<template>
    <div class="wrapper">
        <div class="main visual-2 doc-profile-main">
            <Header />

            <div class="profile-doctor">
                <div class="doctor-container page-content">

                <!-- Main Card -->
                    <div class="wizard-card">

                        <div class="page-header mb-4">
                            <h3 class="page-title"><i class="fa-solid fa-calendar-check"></i> Appointment Types Settings</h3>
                            <p class="text-muted">Manage your appointment types</p>
                        </div>

                      

                        <!-- Action Bar with Create Button -->
                        <div class="action-bar mb-4">
                            <div class="align-items-center">
                                <div class="col-md-6">
                                    <div class="d-flex align-items-center">
                                        <button class="btn-theme-primary" @click="openCreateModal">
                                            <i class="fa-solid fa-plus me-2"></i>
                                            Create New Appointment Type
                                        </button>
                                        <div class="ms-3 text-muted small">
                                            <i class="fa-solid fa-info-circle me-1"></i>
                                            Click to create a new appointment type
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                          <div class="same-day-card ">
                            <div class="same-day-icon">
                                <i class="fa-solid fa-calendar-day"></i>
                            </div>
                            <div class="same-day-content">
                                <h4>Same-Day Appointments</h4>
                                <p>Enable or disable the option for patients to book appointments on the same day.</p>
                            </div>
                            <div class="same-day-control">
                                <label class="switch">
                                    <input
                                        type="checkbox"
                                        v-model="sameDayAppointments"
                                        @change="toggleSameDayAppointments"
                                        :disabled="isLoadingProfile || isUpdatingSameDay"
                                    >
                                    <span class="slider round"></span>
                                </label>
                            </div>
                        </div>

                        <!-- Appointment Types Statistics -->
                        <div class="stats-section mb-4">
                            <div class="row g-3">
                                <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-primary">
                                            <i class="fa-solid fa-calendar-check"></i>
                                        </div>
                                        <div class="stat-content">
                                            <h5>{{ totalAppointmentTypes }}</h5>
                                            <p>Total Types</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-success">
                                            <i class="fa-solid fa-toggle-on"></i>
                                        </div>
                                        <div class="stat-content">
                                            <h5>{{ activeTypes }}</h5>
                                            <p>Active Types</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="stat-card">
                                        <div class="stat-icon bg-danger">
                                            <i class="fa-solid fa-toggle-off"></i>
                                        </div>
                                        <div class="stat-content">
                                            <h5>{{ inactiveTypes }}</h5>
                                            <p>Inactive Types</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Appointment Types List -->
                        <div class="glass-panel">
                            <div class="table-header">
                                <h4 class="panel-title"><i class="fa-solid fa-list me-2"></i> Appointment Types List
                                </h4>
                                <div class="table-filters">
                                    <div class="row g-2">
                                        <div class="col-auto">
                                            <select v-model="statusFilter" class="form-select-sm">
                                                <option value="">All Status</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <select v-model="sortBy" class="form-select-sm">
                                                <option value="label">Sort by Label</option>
                                                <option value="createdAt">Sort by Date</option>
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <select v-model="itemsPerPage" class="form-select-sm">
                                                <option value="10">10 per page</option>
                                                <option value="25">25 per page</option>
                                                <option value="50">50 per page</option>
                                                <option value="100">100 per page</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Loading State -->
                            <div v-if="isLoading" class="text-center py-5">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                                <p class="mt-3 text-muted">Loading appointment types...</p>
                            </div>

                            <!-- Empty State -->
                            <div v-else-if="filteredAppointmentTypes.length === 0" class="empty-state">
                                <div class="empty-icon">
                                    <i class="fa-solid fa-calendar-plus"></i>
                                </div>
                                <h5>No appointment types found</h5>
                                <p class="text-muted">Click "Create New Appointment Type" to add your first type</p>
                                <button class="btn-theme-primary mt-3" @click="openCreateModal">
                                    <i class="fa-solid fa-plus me-2"></i>
                                    Create Your First Appointment Type
                                </button>
                            </div>

                            <!-- Appointment Types Table -->
                            <div v-else class="table-responsive">
                                <table class="table-theme">
                                    <thead>
                                        <tr>
                                            <th>Label</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="type in paginatedAppointmentTypes" :key="type.id">
                                            <td>
                                                <div class="type-label">
                                                    <strong>{{ type.label }}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <div class="type-description" :title="type.description">
                                                    {{ truncateText(type.description, 50) || 'No description' }}
                                                </div>
                                            </td>
                                            <td>
                                                <span class="status-badge" :class="type.active ? 'active' : 'inactive'">
                                                    <i class="fa-solid fa-circle me-2"></i>
                                                    {{ type.active ? 'Active' : 'Inactive' }}
                                                </span>
                                            </td>
                                            <td>
                                                <div class="date-display">
                                                    <i class="fa-solid fa-calendar me-2 text-success"></i>
                                                    {{ formatDate(type.createdAt) }}
                                                </div>
                                            </td>
                                            <td>
                                                <div class="table-actions">
                                                    <button class="btn-action" @click="editAppointmentType(type)" title="Edit">
                                                        <i class="fa-solid fa-edit"></i>
                                                    </button>
                                                    <!-- <button class="btn-action" @click="toggleStatus(type)"
                                                        :title="type.isActive ? 'Deactivate' : 'Activate'">
                                                        <i class="fa-solid"
                                                            :class="type.isActive ? 'fa-toggle-on text-success' : 'fa-toggle-off text-muted'"></i>
                                                    </button> -->
                                                    <button class="btn-action text-danger" @click="deleteAppointmentType(type)"
                                                        title="Delete">
                                                        <i class="fa-solid fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <!-- Pagination -->
                            <div v-if="filteredAppointmentTypes.length > 0" class="table-footer">
                                <div class="row align-items-center">
                                    <div class="col-md-6">
                                        <p class="mb-0 text-muted">
                                            Showing {{ paginatedAppointmentTypes.length }} of {{
                                            filteredAppointmentTypes.length }} appointment types
                                        </p>
                                    </div>
                                    <div class="col-md-6">
                                        <nav aria-label="Page navigation">
                                            <ul class="pagination justify-content-end mb-0">
                                                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                                    <button class="page-link" @click="prevPage">
                                                        <i class="fa-solid fa-chevron-left"></i>
                                                    </button>
                                                </li>

                                                <!-- First page + leading ellipsis -->
                                                <li v-if="visiblePages.length && visiblePages[0] > 1" class="page-item">
                                                    <button class="page-link" @click="currentPage = 1">1</button>
                                                </li>
                                                <li v-if="visiblePages.length && visiblePages[0] > 2" class="page-item disabled">
                                                    <span class="page-link">&hellip;</span>
                                                </li>

                                                <!-- Visible page range -->
                                                <li v-for="page in visiblePages" :key="page" class="page-item"
                                                    :class="{ active: page === currentPage }">
                                                    <button class="page-link" @click="currentPage = page">{{ page
                                                    }}</button>
                                                </li>

                                                <!-- Trailing ellipsis + last page -->
                                                <li v-if="visiblePages.length && visiblePages[visiblePages.length - 1] < totalPages - 1" class="page-item disabled">
                                                    <span class="page-link">&hellip;</span>
                                                </li>
                                                <li v-if="visiblePages.length && visiblePages[visiblePages.length - 1] < totalPages" class="page-item">
                                                    <button class="page-link" @click="currentPage = totalPages">{{ totalPages }}</button>
                                                </li>

                                                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                                    <button class="page-link" @click="nextPage">
                                                        <i class="fa-solid fa-chevron-right"></i>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <FooterView />
        </div>

        <!-- Appointment Type Modal -->
        <AppointmentTypeModal :show="showModal" :appointment-type="selectedAppointmentType" @close="closeModal"
            @saved="handleAppointmentTypeSaved" @deleted="handleAppointmentTypeDeleted"
            @fetch-appointment-types="fetchAppointmentTypes" />
    </div>
</template>

<script>
import Header from "@/layout/Header.vue";
import FooterView from "@/layout/Footer.vue";
import AppointmentTypeModal from "@/components/appointments/CreateAppointmentTypeModal.vue";
import { AppointmentTypeService } from "@/services/appointmentType.service";
import { AuthService } from "@/services/auth.service";

export default {
    name: "AppointmentTypes",
    components: {
        Header,
        FooterView,
        AppointmentTypeModal
    },
    data() {
        return {
            appointmentTypes: [],
            isLoading: false,
            searchQuery: "",
            statusFilter: "",
            sortBy: "label",
            currentPage: 1,
            itemsPerPage: 10,
            selectedHospitalId: localStorage.getItem("hospitalId") || "",
            sameDayAppointments: false,
            isLoadingProfile: false,
            isUpdatingSameDay: false,

            showModal: false,
            selectedAppointmentType: null
        };
    },
    computed: {
        filteredAppointmentTypes() {
            if (!this.appointmentTypes?.length) return [];
            let filtered = this.appointmentTypes;

            // Apply search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(type =>
                    type.label.toLowerCase().includes(query) ||
                    type.code.toLowerCase().includes(query) ||
                    (type.description && type.description.toLowerCase().includes(query))
                );
            }

            // Apply status filter
            if (this.statusFilter === "active") {
                filtered = filtered.filter(type => type.active);
            } else if (this.statusFilter === "inactive") {
                filtered = filtered.filter(type => !type.active);
            }

            // Apply sorting
            filtered.sort((a, b) => {
                if (this.sortBy === "label") {
                    return a?.label?.localeCompare(b?.label);
                } else if (this.sortBy === "createdAt") {
                    return new Date(b?.createdAt) - new Date(a?.createdAt);
                }
                return 0;
            });

            return filtered;
        },

        totalPages() {
            return Math.ceil(this.filteredAppointmentTypes.length / this.itemsPerPage);
        },

        visiblePages() {
            const total = this.totalPages || 1;
            const maxVisible = 7;
            const pages = [];

            if (total <= maxVisible) {
                for (let i = 1; i <= total; i++) pages.push(i);
                return pages;
            }

            const half = Math.floor(maxVisible / 2);
            let start = Math.max(1, this.currentPage - half);
            let end = Math.min(total, this.currentPage + half);

            if (start === 1) end = Math.min(total, maxVisible);
            if (end === total) start = Math.max(1, total - maxVisible + 1);

            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        paginatedAppointmentTypes() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredAppointmentTypes.slice(start, end);
        },

        totalAppointmentTypes() {
            return this.appointmentTypes.length;
        },

        activeTypes() {
            return this.appointmentTypes.length && this.appointmentTypes?.filter(type => type.active).length;
        },

        inactiveTypes() {
            return this.appointmentTypes.length && this.appointmentTypes?.filter(type => !type.active).length;
        },

        averageDuration() {
            if (this.appointmentTypes?.length === 0) return "0m";
            const total = this.appointmentTypes.length && this.appointmentTypes.reduce((sum, type) =>
                sum + (type.defaultDuration || 30), 0);
            const average = Math.round(total / this.appointmentTypes.length);
            return `${average}m`;
        },

        uniqueColors() {
            const colors = this.appointmentTypes.length && new Set(this.appointmentTypes.map(type => type.color));
            return colors?.size;
        }
    },
    watch: {
        totalPages(newVal) {
            const pages = newVal || 1;
            if (this.currentPage > pages) this.currentPage = pages;
            if (this.currentPage < 1) this.currentPage = 1;
        }
    },

    async mounted() {
        await this.loadProfileSettings();
        this.fetchAppointmentTypes();
    },
    methods: {
        getActiveHospitalId() {
            const user = this.$store?.state?.user?.currentUser || {};

            return (
                this.selectedHospitalId ||
                user?.profile?.id ||
                user?.profile?._id ||
                user?.hospitalId ||
                user?.objectId ||
                localStorage.getItem("hospitalId") ||
                ""
            );
        },

        applySameDayAppointmentsFromUser(user) {
            const sameDayAppointments =
                user?.profile?.sameDayAppointments ??
                user?.hospital?.sameDayAppointments;

            if (sameDayAppointments !== undefined) {
                this.sameDayAppointments = sameDayAppointments;
            }

            const hospitalId =
                user?.profile?.id ||
                user?.profile?._id ||
                user?.hospitalId ||
                user?.objectId;

            if (hospitalId) {
                this.selectedHospitalId = hospitalId;
                localStorage.setItem("hospitalId", hospitalId);
            }
        },

        async loadProfileSettings() {
            this.isLoadingProfile = true;
            try {
                const response = await AuthService.getProfile();
                this.applySameDayAppointmentsFromUser(response.data);
            } catch (error) {
                const user = this.$store?.state?.user?.currentUser;
                this.applySameDayAppointmentsFromUser(user);
            } finally {
                this.isLoadingProfile = false;
            }
        },

        async toggleSameDayAppointments() {
            const hospitalId = this.getActiveHospitalId();
            if (!hospitalId) {
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: "No active hospital selected"
                });
                return;
            }

            this.isUpdatingSameDay = true;
            try {
                const response = await AuthService.updateSameDayAppointments(
                    hospitalId,
                    this.sameDayAppointments
                );

                if (response.success || response.status === "200") {
                    this.$store.commit("toast/NEW", {
                        type: "success",
                        message: response.message || "Same-day appointments setting updated."
                    });

                    const profileResponse = await AuthService.getProfile();
                    this.applySameDayAppointmentsFromUser(profileResponse.data);
                }
            } catch (error) {
                this.sameDayAppointments = !this.sameDayAppointments;
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: "Failed to update same-day appointments setting."
                });
            } finally {
                this.isUpdatingSameDay = false;
            }
        },

        async fetchAppointmentTypes() {
            this.isLoading = true;
            try {
                const hospitalId = this.getActiveHospitalId();
                const response = await AppointmentTypeService.getAll({ hospitalId });

                const data = response.data;

                console.log("11", data);
                if (data.status === 200) {
                    console.log("22", data.data);
                    this.appointmentTypes = data.data.data || [];
                } else {
                    this.$store.commit("toast/NEW", {
                        type: "error",
                        message: data.message || "Failed to fetch appointment types"
                    });
                }
            } catch (error) {
                console.error("Error fetching appointment types:", error);
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: "An error occurred while fetching appointment types"
                });
            } finally {
                this.isLoading = false;
            }
        },

        openCreateModal() {
            this.selectedAppointmentType = null;
            this.showModal = true;
        },

        editAppointmentType(type) {
            this.selectedAppointmentType = { ...type };
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.selectedAppointmentType = null;
        },

        handleAppointmentTypeSaved(savedType) {
            // Update local appointment types list
            const index = this.appointmentTypes.findIndex(t => t.id === savedType.id);
            if (index !== -1) {
                // Update existing type
                this.appointmentTypes.splice(index, 1, savedType);
            } else {
                // Add new type
                this.appointmentTypes.unshift(savedType);
            }
        },

        handleAppointmentTypeDeleted(typeId) {
            // Remove from local list
            this.appointmentTypes = this.appointmentTypes.length && this.appointmentTypes?.filter(t => t.id !== typeId);
        },

        // async toggleStatus(type) {
        //     try {
        //         const newStatus = !type.isActive;
        //         const response = await AppointmentTypeService.updateStatus(type.id, newStatus);

        //         if (response.data.status === 200) {
        //             this.$store.commit("toast/NEW", {
        //                 type: "success",
        //                 message: `Appointment type ${newStatus ? 'activated' : 'deactivated'} successfully!`
        //             });

        //             // Update local state
        //             type.isActive = newStatus;
        //         } else {
        //             this.$store.commit("toast/NEW", {
        //                 type: "error",
        //                 message: response.data.message || "Failed to update status"
        //             });
        //         }
        //     } catch (error) {
        //         console.error("Error updating status:", error);
        //         this.$store.commit("toast/NEW", {
        //             type: "error",
        //             message: "An error occurred while updating status"
        //         });
        //     }
        // },

        async deleteAppointmentType(type) {
            if (!confirm(`Are you sure you want to delete "${type.label}"? This action cannot be undone.`)) {
                return;
            }

            try {
                const response = await AppointmentTypeService.delete(type.id);

                if (response.data.status === 200) {
                    this.$store.commit("toast/NEW", {
                        type: "success",
                        message: response?.data?.data?.message || "Appointment type deleted successfully!"
                    });

                    // Remove from local list
                    this.appointmentTypes = this.appointmentTypes.length && this.appointmentTypes?.filter(t => t.id !== type.id);
                } else {
                    this.$store.commit("toast/NEW", {
                        type: "error",
                        message: response?.data?.data?.message || "Failed to delete appointment type"
                    });
                }
            } catch (error) {
                console.error("Error deleting appointment type:", error);
                const errorMsg = error?.response?.data
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: errorMsg?.data?.data?.message || errorMsg?.data?.message || "An error occurred while deleting appointment type"
                });
            }
        },

        truncateText(text, length) {
            if (!text) return "";
            if (text.length <= length) return text;
            return text.substring(0, length) + "...";
        },

        formatDate(dateString) {
            if (!dateString) return "N/A";
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
        ,

        prevPage() {
            if (this.currentPage > 1) this.currentPage--;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) this.currentPage++;
        }
    }
};
</script>

<style scoped>
.page-title {
    font-size: 24px;
    font-weight: 600;
    color: #0144A6;
}

.wrapper,
.main,
.profile-doctor {
    height: 100vh;
}

.doctor-container {
    height: 50vh;
}

.page-content {
    width: 100%;
    margin: 150px auto;
    padding-bottom: 50px;
}

.wizard-card {
    width: 100%;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    padding: 30px;
    height: calc(100vh - 160px);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
}

.page-header {
    width: 100%;
}

.same-day-card {
    align-items: center;
    background: #ffffff;
    border: 1px solid #eef2f6;
    border-radius: 12px;
    display: grid;
    gap: 18px;
    grid-template-columns: auto 1fr auto;
    padding: 20px;
}

.same-day-icon {
    align-items: center;
    background: rgba(1, 68, 166, 0.1);
    border-radius: 12px;
    color: #0144A6;
    display: flex;
    font-size: 24px;
    height: 54px;
    justify-content: center;
    width: 54px;
}

.same-day-content h4 {
    color: #0144A6;
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 5px;
}

.same-day-content p {
    color: #666;
    font-size: 14px;
    margin: 0;
}

.switch {
    display: inline-block;
    height: 28px;
    position: relative;
    width: 54px;
}

.switch input {
    height: 0;
    opacity: 0;
    width: 0;
}

.slider {
    background-color: #ccc;
    bottom: 0;
    cursor: pointer;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: .4s;
}

.slider:before {
    background-color: white;
    bottom: 4px;
    content: "";
    height: 20px;
    left: 4px;
    position: absolute;
    transition: .4s;
    width: 20px;
}

input:checked + .slider {
    background-color: #28a745;
}

input:focus + .slider {
    box-shadow: 0 0 1px #28a745;
}

input:checked + .slider:before {
    transform: translateX(26px);
}

.slider.round {
    border-radius: 28px;
}

.slider.round:before {
    border-radius: 50%;
}

/* Conversation Header */
.conversation-header {
    display: flex;
    gap: 15px;
    align-items: flex-start;
    background: #f8faff;
    padding: 15px;
    border-radius: 10px;
    border-left: 4px solid #3c78f9;
}

.avatar-assistant {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #3c78f9;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.assistant-bubble {
    font-size: 16px;
    color: #333;
    font-weight: 500;
    line-height: 1.4;
}

/* Action Bar */
.action-bar {
    background: #f8faff;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid #eef2f6;
}

.btn-theme-primary {
    background: #3c78f9;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
}

.btn-theme-primary:hover {
    background: #2a65e0;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(60, 120, 249, 0.3);
}

.input-group-text {
    background: white;
    border: 1px solid #ddd;
    border-right: none;
    color: #666;
}

/* Glass Panel */
.glass-panel {
    background: #ffffff;
    border: 1px solid #eee;
    border-radius: 20px;
    padding: 30px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    margin-bottom: 30px;
}

.panel-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0144A6;
    margin-bottom: 0;
    display: flex;
    align-items: center;
}

/* Table Header */
.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eef2f6;
}

.table-filters .form-select-sm {
    padding: 6px 12px;
    font-size: 14px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    min-width: 140px;
}

/* Statistics Cards */
.stats-section {
    margin-bottom: 30px;
}

.stat-card {
    background: white;
    border: 1px solid #eef2f6;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    transition: all 0.3s ease;
    height: 100%;
}

.stat-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.stat-icon {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
}

.stat-icon.bg-primary {
    background: #3c78f9;
}

.stat-icon.bg-success {
    background: #28a745;
}

.stat-icon.bg-info {
    background: #17a2b8;
}

.stat-icon.bg-warning {
    background: #ffc107;
}

.stat-content h5 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #0144A6;
}

.stat-content p {
    margin: 5px 0 0 0;
    color: #666;
    font-size: 13px;
}

/* Appointment Type specific styles */
.type-badge {
    padding: 6px 12px;
    border-radius: 20px;
    color: white;
    font-weight: 700;
    font-size: 12px;
    display: inline-block;
    text-align: center;
    min-width: 60px;
}

.type-label {
    font-weight: 600;
    color: #333;
    font-size: 15px;
}

.type-description {
    color: #666;
    font-size: 14px;
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.duration-badge {
    background: #f0f5ff;
    color: #0144A6;
    padding: 6px 12px;
    border-radius: 15px;
    font-weight: 600;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
}

.status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
}

.status-badge.active {
    background: #e6fff2;
    color: #0d6e42;
}

.status-badge.inactive {
    background: #f8f9fa;
    color: #666;
}

.date-display {
    color: #666;
    font-size: 14px;
    display: flex;
    align-items: center;
}

.table-theme {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.table-theme thead {
    background: #f8faff;
}

.table-theme th {
    padding: 15px;
    text-align: left;
    font-weight: 600;
    color: #0144A6;
    border-bottom: 2px solid #eef2f6;
    font-size: 14px;
}

.table-theme td {
    padding: 15px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
}

.table-theme tbody tr:hover {
    background: #f8faff;
}

.table-theme tbody tr:last-child td {
    border-bottom: none;
}

.table-actions {
    display: flex;
    gap: 8px;
}

.btn-action {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    transition: all 0.2s;
    cursor: pointer;
}

.btn-action:hover {
    background: #f8faff;
    border-color: #3c78f9;
    color: #3c78f9;
}

.btn-action.text-danger:hover {
    background: #fff5f5;
    border-color: #dc3545;
    color: #dc3545;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #adb5bd;
}

.empty-icon {
    font-size: 4rem;
    margin-bottom: 20px;
    opacity: 0.5;
}

.empty-state h5 {
    color: #495057;
    margin-bottom: 10px;
}

/* Table Footer */
.table-footer {
    border-top: 1px solid #eee;
    padding-top: 20px;
    margin-top: 20px;
}

.pagination {
    margin-bottom: 0;
}

.page-link {
    border: 1px solid #e0e0e0;
    color: #666;
    padding: 8px 16px;
    margin: 0 4px;
    border-radius: 8px;
    background: white;
}

.page-item.active .page-link {
    background-color: #3c78f9;
    border-color: #3c78f9;
    color: white;
}

.page-link:hover {
    background-color: #f8faff;
    border-color: #3c78f9;
    color: #3c78f9;
}

.page-item.disabled .page-link {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 992px) {
    .page-header {
        width: 100%;
    }

    .action-bar .row {
        flex-direction: column;
        gap: 15px;
    }

    .table-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .table-filters .row {
        flex-wrap: wrap;
    }

    .table-responsive {
        overflow-x: auto;
    }

    .table-theme {
        min-width: 800px;
    }

    .stats-section .col-md-3 {
        margin-bottom: 15px;
    }
}

@media (max-width: 768px) {
    .same-day-card {
        grid-template-columns: 1fr;
        text-align: center;
    }

    .same-day-icon {
        margin: 0 auto;
    }

    .stat-card {
        flex-direction: column;
        text-align: center;
        padding: 15px;
    }
}
</style>
